import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Package,
  AlertTriangle,
  FileText,
  Plus,
  Clock,
  Banknote,
  TrendingUp,
  ShoppingCart,
  Receipt,
  Warehouse,
  ArrowLeftRight,
  ShieldCheck,
  ScrollText,
  Landmark,
  UserRound,
  CalendarClock,
  Coins,
  Building2,
  CalendarDays,
} from 'lucide-react';
import { invoiceReferenceLabel } from '@efacture/core';
import { type DashboardWidgetKey } from '@efacture/core/sector';
import { CONTRACT_TYPE_LABELS, isContractType } from '@efacture/accounting';

import { Button } from '@/components/ui/button';
import { KPICard } from '@/components/dashboard/KPICard';
import { AlertsBanner } from '@/components/dashboard/AlertsBanner';
import { useTranslation } from '@/lib/i18n';
import type { ClientStatistics } from '@/api/queries/clients';
import type { ArticleStatistics, Article } from '@/api/queries/articles';
import type {
  DashboardInvoiceStats,
  DashboardSummaryBlocks,
} from '@/api/queries/dashboard';
import { PAYROLL_RUN_STATUS_LABELS, type PayrollRunStatus } from '@/api/queries/hr';
import { useMoneyFormatter } from '@/hooks/useMoneyFormatter';

/**
 * The dashboard blocks, one component per catalogue key.
 *
 * The queries stay in the page so that a fixed number of requests serve
 * any number of blocks; every widget reads from this one payload.
 */
export interface DashboardWidgetProps {
  clientStats?: ClientStatistics | undefined;
  clientStatsLoading: boolean;
  articleStats?: ArticleStatistics | undefined;
  articleStatsLoading: boolean;
  lowStockArticles?: Article[] | undefined;
  lowStockLoading: boolean;
  invoiceStats?: DashboardInvoiceStats | undefined;
  invoiceStatsLoading: boolean;
  summaryBlocks?: DashboardSummaryBlocks | undefined;
}

/**
 * Which query feeds a widget. The page fetches a source only when a
 * visible widget needs it, so a till operator no longer asks for the
 * client statistics its screen never shows.
 */
export type DashboardSource =
  | 'clients'
  | 'articles'
  | 'lowStock'
  | 'invoices'
  | 'summary';

/**
 * The permission a source's endpoint demands; `null` when it demands
 * none. `/dashboard/summary` authenticates and gates each BLOCK on its
 * own permission, so the request itself demands nothing.
 */
export const SOURCE_PERMISSION: Readonly<
  Record<DashboardSource, string | null>
> = {
  clients: 'clients:read',
  articles: 'articles:read',
  lowStock: 'articles:read',
  invoices: null,
  summary: null,
};

/**
 * Shortages, counted from the list when it is loaded and from the
 * statistics otherwise. Zero when neither is: without `articles:read`
 * both queries stay unsent, and no alert claims a stock is healthy.
 */
function lowStockCountOf(props: DashboardWidgetProps): number {
  return props.lowStockArticles?.length ?? props.articleStats?.lowStockCount ?? 0;
}

function StatRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="text-sm font-semibold text-slate-900">
        {value.toLocaleString('fr-FR')}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Widgets
// ─────────────────────────────────────────────────────────────

function AlertsWidget(props: DashboardWidgetProps) {
  return (
    <AlertsBanner
      lowStockCount={lowStockCountOf(props)}
      overdueInvoicesCount={props.invoiceStats?.overdueInvoicesCount ?? 0}
      loading={
        props.articleStatsLoading || props.lowStockLoading || props.invoiceStatsLoading
      }
    />
  );
}

function KpiClientsWidget({ clientStats, clientStatsLoading }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <KPICard
      title={t('dashboard.kpi.activeClients')}
      value={clientStats?.activeClients ?? 0}
      icon={Users}
      description={`${clientStats?.totalClients ?? 0} ${t('dashboard.kpi.totalClients')}`}
      loading={clientStatsLoading}
      onClick={() => navigate('/clients')}
    />
  );
}

function KpiArticlesWidget({ articleStats, articleStatsLoading }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <KPICard
      title={t('dashboard.kpi.activeArticles')}
      value={articleStats?.activeArticles ?? 0}
      icon={Package}
      description={`${articleStats?.totalArticles ?? 0} ${t('dashboard.kpi.totalArticles')}`}
      loading={articleStatsLoading}
      onClick={() => navigate('/articles')}
    />
  );
}

function KpiLowStockWidget(props: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lowStockCount = lowStockCountOf(props);
  return (
    <KPICard
      title={t('dashboard.kpi.lowStock')}
      value={lowStockCount}
      icon={AlertTriangle}
      description={t('dashboard.kpi.needRestock')}
      variant={lowStockCount > 0 ? 'warning' : 'default'}
      loading={props.lowStockLoading}
      onClick={() => navigate('/articles?filter=low-stock')}
    />
  );
}

function KpiMonthInvoicesWidget({
  invoiceStats,
  invoiceStatsLoading,
}: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const overdueInvoicesCount = invoiceStats?.overdueInvoicesCount ?? 0;
  return (
    <KPICard
      title={t('dashboard.kpi.monthInvoices')}
      value={invoiceStats?.invoicesThisMonth ?? 0}
      icon={FileText}
      description={
        overdueInvoicesCount > 0
          ? `${overdueInvoicesCount} ${t('dashboard.kpi.overdueInvoices')}`
          : t('dashboard.kpi.signedThisMonth')
      }
      variant={overdueInvoicesCount > 0 ? 'warning' : 'default'}
      loading={invoiceStatsLoading}
      onClick={() => navigate('/invoices')}
    />
  );
}

function QuickActionsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900">
        {t('dashboard.quickActions.title')}
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        {t('dashboard.quickActions.subtitle')}
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <Button
          onClick={() => navigate('/invoices')}
          className="bg-gradient-to-r from-brand-lagune-700 to-brand-lagune-800 hover:from-brand-lagune-800 hover:to-brand-lagune-900"
        >
          <Plus className="h-4 w-4" />
          {t('dashboard.quickActions.newInvoice')}
        </Button>
        <Button onClick={() => navigate('/clients')} variant="outline">
          <Users className="h-4 w-4" />
          {t('dashboard.quickActions.newClient')}
        </Button>
        <Button onClick={() => navigate('/articles')} variant="outline">
          <Package className="h-4 w-4" />
          {t('dashboard.quickActions.newArticle')}
        </Button>
      </div>
    </div>
  );
}

function RecentInvoicesWidget({
  invoiceStats,
  invoiceStatsLoading,
}: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900">
        {t('dashboard.recentInvoices.title')}
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        {t('dashboard.recentInvoices.subtitle')}
      </p>
      {invoiceStatsLoading ? (
        <div className="mt-4 space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded bg-slate-200" />
          ))}
        </div>
      ) : invoiceStats?.recentInvoices && invoiceStats.recentInvoices.length > 0 ? (
        <div className="mt-4 divide-y divide-slate-100">
          {invoiceStats.recentInvoices.map((inv) => (
            <div
              key={inv.id}
              className="flex cursor-pointer items-center justify-between py-3 hover:bg-slate-50 rounded-lg px-2 transition-colors"
              onClick={() => navigate(`/invoices/${inv.id}`)}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-lagune-100">
                  <FileText className="h-4 w-4 text-brand-lagune-700" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{invoiceReferenceLabel(inv.number)}</p>
                  <p className="text-xs text-slate-500">
                    {inv.clientName ?? t('dashboard.recentInvoices.cashClient')}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-900">
                  {money(inv.totalTTC, inv.currency)}
                </p>
                <p className="text-xs text-slate-500">
                  {new Date(inv.createdAt).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center py-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Clock className="h-6 w-6 text-slate-400" />
          </div>
          <p className="mt-4 text-sm text-slate-500">
            {t('dashboard.recentInvoices.empty')}
          </p>
        </div>
      )}
    </div>
  );
}

function ClientBreakdownWidget({
  clientStats,
  clientStatsLoading,
}: DashboardWidgetProps) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-900">
        {t('dashboard.stats.clientBreakdown')}
      </h3>
      <div className="mt-4 space-y-3">
        {clientStatsLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-slate-200" />
            ))}
          </div>
        ) : (
          <>
            <StatRow label={t('dashboard.stats.clientCC')} value={clientStats?.byType.CC ?? 0} />
            <StatRow label={t('dashboard.stats.clientPM')} value={clientStats?.byType.PM ?? 0} />
            <StatRow label={t('dashboard.stats.clientPP')} value={clientStats?.byType.PP ?? 0} />
            <StatRow label={t('dashboard.stats.clientPC')} value={clientStats?.byType.PC ?? 0} />
          </>
        )}
      </div>
    </div>
  );
}

function ArticleBreakdownWidget({
  articleStats,
  articleStatsLoading,
}: DashboardWidgetProps) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-900">
        {t('dashboard.stats.articleBreakdown')}
      </h3>
      <div className="mt-4 space-y-3">
        {articleStatsLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-slate-200" />
            ))}
          </div>
        ) : (
          <>
            <StatRow label={t('dashboard.stats.goods')} value={articleStats?.biens ?? 0} />
            <StatRow label={t('dashboard.stats.services')} value={articleStats?.services ?? 0} />
            <StatRow
              label={t('dashboard.stats.favorites')}
              value={articleStats?.favoriteCount ?? 0}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Blocks served by /dashboard/summary
//
// Each one renders only when its block is in the payload. Absent means
// refused, or a module that is off, or nothing to stand on — and none of
// those is a zero, so none of them draws a card.
// ─────────────────────────────────────────────────────────────

/**
 * « autres monnaies, non additionnées : 11,80 EUR ; … », or nothing when
 * every figure is in the block's own currency.
 */
function otherCurrenciesNote(amounts: string[]): string {
  return amounts.length === 0 ? '' : ` — autres monnaies, non additionnées : ${amounts.join(' ; ')}`;
}

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  );
}

function TextRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="text-sm font-semibold text-slate-900">{value}</span>
    </div>
  );
}

const EMPTY = <></>;

function RevenueWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const navigate = useNavigate();
  const block = summaryBlocks?.['kpi-revenue'];
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.revenue.title')}
      value={money(block.totalTTC, block.currency)}
      icon={Banknote}
      description={`${block.invoiceCount.toLocaleString('fr-FR')} ${t('dashboard.revenue.description')}${otherCurrenciesNote(
        block.otherCurrencies.map((other) => money(other.totalTTC, other.currency))
      )}`}
      onClick={() => navigate('/reports/sales')}
    />
  );
}

function ReceivablesWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const navigate = useNavigate();
  const block = summaryBlocks?.['kpi-receivables'];
  if (!block) return EMPTY;
  // « dont N en retard » asserts containment: N is a SUBSET of the count
  // above, computed in the same scan as the total shown on the card.
  const description =
    (block.overdueCount > 0
      ? `${block.totalCount} ${t('dashboard.receivables.description')} — ${t('dashboard.receivables.overdue')} ${block.overdueCount} (${money(block.overdueRemaining, block.currency)})`
      : `${block.totalCount} ${t('dashboard.receivables.description')}`) +
    otherCurrenciesNote(block.otherCurrencies.map((other) => money(other.totalRemaining, other.currency)));
  return (
    <KPICard
      title={t('dashboard.receivables.title')}
      value={money(block.totalRemaining, block.currency)}
      icon={Receipt}
      description={description}
      variant={block.overdueCount > 0 ? 'warning' : 'default'}
      onClick={() => navigate('/invoices?payment=PENDING')}
    />
  );
}

function TopArticlesWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const block = summaryBlocks?.['top-articles'];
  if (!block) return EMPTY;
  // Each currency is ranked on its own: two currencies are never compared.
  const rankings = [block, ...block.otherCurrencies].filter((ranking) => ranking.articles.length > 0);
  if (rankings.length === 0) return EMPTY;
  return (
    <Panel
      title={t('dashboard.topArticles.title')}
      subtitle={t('dashboard.topArticles.subtitle')}
    >
      {rankings.map((ranking, index) => (
        <div key={ranking.currency} className="space-y-3">
          {index > 0 && (
            <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Monnaie {ranking.currency}
            </p>
          )}
          {ranking.articles.map((article) => (
            <TextRow
              key={article.id}
              label={`${article.name} (${article.quantity.toLocaleString('fr-FR')})`}
              value={money(article.total, ranking.currency)}
            />
          ))}
        </div>
      ))}
    </Panel>
  );
}

function MySalesWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const block = summaryBlocks?.['my-sales'];
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.mySales.title')}
      value={money(block.todayTotalTTC, block.currency)}
      icon={ShoppingCart}
      description={`${block.todayInvoiceCount} ${t('dashboard.mySales.description')}${otherCurrenciesNote(
        block.otherCurrencies
          .filter((other) => other.todayInvoiceCount > 0)
          .map((other) => money(other.todayTotalTTC, other.currency))
      )}`}
    />
  );
}

function TillWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const block = summaryBlocks?.['till-today'];
  if (!block) return EMPTY;
  return (
    <Panel title={t('dashboard.till.title')} subtitle={t('dashboard.till.subtitle')}>
      <TextRow label={t('dashboard.till.opening')} value={money(block.openingBalance)} />
      <TextRow label={t('dashboard.till.sales')} value={money(block.totalSales)} />
      <TextRow label={t('dashboard.till.deposits')} value={money(block.totalDeposits)} />
      <TextRow
        label={t('dashboard.till.withdrawals')}
        value={money(block.totalWithdrawals)}
      />
      <TextRow label={t('dashboard.till.refunds')} value={money(block.totalRefunds)} />
      <TextRow
        label={t('dashboard.till.expected')}
        value={money(block.expectedBalance)}
      />
    </Panel>
  );
}

function PendingTicketsWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const block = summaryBlocks?.['pending-tickets'];
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.pendingTickets.title')}
      value={block.total}
      icon={Clock}
      description={`${block.suspended} ${t('dashboard.pendingTickets.description')}`}
      variant={block.total > 0 ? 'warning' : 'default'}
      onClick={() => navigate('/pos')}
    />
  );
}

function StockOverviewWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const navigate = useNavigate();
  const block = summaryBlocks?.['stock-overview'];
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.stock.title')}
      value={money(block.totalValue)}
      icon={Warehouse}
      description={`${block.stockManagedArticles} ${t('dashboard.stock.description')}`}
      variant={block.lowStockCount > 0 ? 'warning' : 'default'}
      onClick={() => navigate('/stock')}
    />
  );
}

function NearExpiryWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const block = summaryBlocks?.['near-expiry'];
  if (!block || block.batches.length === 0) return EMPTY;
  return (
    <Panel
      title={t('dashboard.nearExpiry.title')}
      subtitle={t('dashboard.nearExpiry.subtitle')}
    >
      {block.batches.map((batch) => (
        <TextRow
          key={batch.id}
          label={`${batch.articleName} — ${batch.batchNumber}`}
          value={`${batch.daysUntilExpiration} ${t('dashboard.nearExpiry.days')}`}
        />
      ))}
    </Panel>
  );
}

function StockMovementsWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const block = summaryBlocks?.['stock-movements-today'];
  if (!block) return EMPTY;
  const detail = Object.entries(block.byType)
    .map(([type, count]) => `${type} ${count}`)
    .join(' · ');
  return (
    <KPICard
      title={t('dashboard.movements.title')}
      value={block.total}
      icon={ArrowLeftRight}
      description={detail || t('dashboard.movements.description')}
      onClick={() => navigate('/stock/movements')}
    />
  );
}

function VerificationQueueWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const block = summaryBlocks?.['verification-queue'];
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.verification.title')}
      value={block.pending}
      icon={ShieldCheck}
      description={t('dashboard.verification.description')}
      variant={block.pending > 0 ? 'warning' : 'default'}
      onClick={() => navigate('/invoices/verification')}
    />
  );
}

function McfDoubtWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const block = summaryBlocks?.['mcf-doubt'];
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.mcfDoubt.title')}
      value={block.total}
      icon={ScrollText}
      description={t('dashboard.mcfDoubt.description')}
      variant={block.total > 0 ? 'danger' : 'default'}
      onClick={() => navigate('/invoices/signatures-en-doute')}
    />
  );
}

function TreasuryWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const block = summaryBlocks?.treasury;
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.treasury.title')}
      value={money(block.total)}
      icon={Landmark}
      description={t('dashboard.treasury.description')}
      variant={block.total < 0 ? 'danger' : 'default'}
    />
  );
}

/**
 * Rendered only where perpetual inventory is on — the server omits the
 * block otherwise, and a margin equal to the whole revenue would be read
 * as a measurement.
 */
function MarginWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const block = summaryBlocks?.['kpi-margin'];
  if (!block) return EMPTY;
  const rate =
    block.marginRate === null
      ? t('dashboard.margin.description')
      : `${(block.marginRate * 100).toFixed(1)} % — ${t('dashboard.margin.description')}`;
  return (
    <KPICard
      title={t('dashboard.margin.title')}
      value={money(block.margin, block.currency)}
      icon={TrendingUp}
      description={rate}
      variant={block.margin < 0 ? 'danger' : 'success'}
    />
  );
}

function AccountingHealthWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const block = summaryBlocks?.['accounting-health'];
  if (!block) return EMPTY;
  return (
    <Panel
      title={t('dashboard.accounting.title')}
      subtitle={`${t('dashboard.accounting.subtitle')} ${block.year}`}
    >
      <TextRow
        label={t('dashboard.accounting.draftEntries')}
        value={block.draftEntries.toLocaleString('fr-FR')}
      />
      <TextRow
        label={t('dashboard.accounting.outboxPending')}
        value={block.outboxPending.toLocaleString('fr-FR')}
      />
      <TextRow
        label={t('dashboard.accounting.outboxFailed')}
        value={block.outboxFailed.toLocaleString('fr-FR')}
      />
      <TextRow
        label={t('dashboard.accounting.periodsLocked')}
        value={`${block.periodsLocked.locked} / ${block.periodsLocked.total}`}
      />
      <TextRow
        label={t('dashboard.accounting.balanced')}
        value={
          block.balanced === null
            ? t('dashboard.common.notDeterminable')
            : block.balanced
              ? t('dashboard.common.yes')
              : t('dashboard.common.no')
        }
      />
      {block.openPeriodMonth !== null && (
        <TextRow
          label={t('dashboard.accounting.openPeriod')}
          value={`${block.openPeriodMonth}/${block.year}`}
        />
      )}
    </Panel>
  );
}

function HeadcountWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const block = summaryBlocks?.['hr-headcount'];
  if (!block) return EMPTY;
  const detail = Object.entries(block.byContractType)
    .map(([type, count]) => `${isContractType(type) ? CONTRACT_TYPE_LABELS[type] : type} ${count}`)
    .join(' · ');
  return (
    <KPICard
      title={t('dashboard.hr.headcount.title')}
      value={block.active}
      icon={UserRound}
      description={detail || t('dashboard.hr.headcount.description')}
      onClick={() => navigate('/rh/employes')}
    />
  );
}

function LeaveWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const block = summaryBlocks?.['hr-leave'];
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.hr.leave.title')}
      value={block.pending}
      icon={CalendarDays}
      description={`${block.approvedDaysThisYear.toLocaleString('fr-FR')} ${t('dashboard.hr.leave.description')}`}
      variant={block.pending > 0 ? 'warning' : 'default'}
      onClick={() => navigate('/rh/conges')}
    />
  );
}

function ContractsWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const block = summaryBlocks?.['hr-contracts'];
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.hr.contracts.title')}
      value={block.total}
      icon={CalendarClock}
      description={t('dashboard.hr.contracts.description')}
      variant={block.total > 0 ? 'warning' : 'default'}
      onClick={() => navigate('/rh/employes')}
    />
  );
}

function PayrollWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const block = summaryBlocks?.['payroll-status'];
  if (!block) return EMPTY;
  return (
    <Panel
      title={t('dashboard.payroll.title')}
      subtitle={`${t('dashboard.payroll.subtitle')} ${block.year}`}
    >
      {Object.entries(block.byStatus).map(([status, count]) => (
        <TextRow
          key={status}
          label={PAYROLL_RUN_STATUS_LABELS[status as PayrollRunStatus] ?? status}
          value={count.toLocaleString('fr-FR')}
        />
      ))}
      <TextRow label={t('dashboard.payroll.gross')} value={money(block.totalGross)} />
      <TextRow label={t('dashboard.payroll.net')} value={money(block.totalNet)} />
      <TextRow
        label={t('dashboard.payroll.employerCharges')}
        value={money(block.totalEmployerCharges)}
      />
    </Panel>
  );
}

function BudgetExecutionWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const navigate = useNavigate();
  const block = summaryBlocks?.['budget-execution'];
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.budget.title')}
      value={money(block.engaged)}
      icon={Coins}
      description={`${block.envelopeCount} enveloppe(s) — ${t('dashboard.budget.description')} ${block.year}`}
      onClick={() => navigate('/budget/execution')}
    />
  );
}

function BudgetQueuesWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const money = useMoneyFormatter();
  const block = summaryBlocks?.['budget-queues'];
  if (!block) return EMPTY;
  return (
    <Panel
      title={t('dashboard.budget.queues.title')}
      subtitle={t('dashboard.budget.queues.subtitle')}
    >
      {/* The visa step exists only under public execution. */}
      {block.publicExecution && (
        <TextRow
          label={t('dashboard.budget.queues.awaitingVisa')}
          value={block.awaitingVisa.toLocaleString('fr-FR')}
        />
      )}
      <TextRow
        label={t('dashboard.budget.queues.toOrder')}
        value={money(block.toOrder)}
      />
      <TextRow label={t('dashboard.budget.queues.toPay')} value={money(block.toPay)} />
    </Panel>
  );
}

function EstablishmentsWidget({ summaryBlocks }: DashboardWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const block = summaryBlocks?.establishments;
  if (!block) return EMPTY;
  return (
    <KPICard
      title={t('dashboard.establishments.title')}
      value={block.total}
      icon={Building2}
      description={`${block.active} ${t('dashboard.establishments.description')}`}
      onClick={() => navigate('/admin/establishments')}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// The key → component table
// ─────────────────────────────────────────────────────────────

export interface DashboardWidgetComponent {
  Component: (props: DashboardWidgetProps) => ReactElement;
  needs: readonly DashboardSource[];
}

export const DASHBOARD_WIDGET_COMPONENTS: Readonly<
  Record<DashboardWidgetKey, DashboardWidgetComponent>
> = {
  alerts: { Component: AlertsWidget, needs: ['articles', 'lowStock', 'invoices'] },
  'kpi-clients': { Component: KpiClientsWidget, needs: ['clients'] },
  'kpi-articles': { Component: KpiArticlesWidget, needs: ['articles'] },
  'kpi-low-stock': {
    Component: KpiLowStockWidget,
    needs: ['articles', 'lowStock'],
  },
  'kpi-month-invoices': {
    Component: KpiMonthInvoicesWidget,
    needs: ['invoices'],
  },
  'quick-actions': { Component: QuickActionsWidget, needs: [] },
  'recent-invoices': { Component: RecentInvoicesWidget, needs: ['invoices'] },
  'client-breakdown': { Component: ClientBreakdownWidget, needs: ['clients'] },
  'article-breakdown': {
    Component: ArticleBreakdownWidget,
    needs: ['articles'],
  },

  // One request serves all of these, and `needs: ['summary']` is also
  // what marks a key as summary-backed for the page.
  'kpi-revenue': { Component: RevenueWidget, needs: ['summary'] },
  'kpi-receivables': { Component: ReceivablesWidget, needs: ['summary'] },
  'top-articles': { Component: TopArticlesWidget, needs: ['summary'] },
  'my-sales': { Component: MySalesWidget, needs: ['summary'] },
  'till-today': { Component: TillWidget, needs: ['summary'] },
  'pending-tickets': { Component: PendingTicketsWidget, needs: ['summary'] },
  'stock-overview': { Component: StockOverviewWidget, needs: ['summary'] },
  'near-expiry': { Component: NearExpiryWidget, needs: ['summary'] },
  'stock-movements-today': {
    Component: StockMovementsWidget,
    needs: ['summary'],
  },
  'verification-queue': {
    Component: VerificationQueueWidget,
    needs: ['summary'],
  },
  'mcf-doubt': { Component: McfDoubtWidget, needs: ['summary'] },
  treasury: { Component: TreasuryWidget, needs: ['summary'] },
  'kpi-margin': { Component: MarginWidget, needs: ['summary'] },
  'accounting-health': {
    Component: AccountingHealthWidget,
    needs: ['summary'],
  },
  'hr-headcount': { Component: HeadcountWidget, needs: ['summary'] },
  'hr-leave': { Component: LeaveWidget, needs: ['summary'] },
  'hr-contracts': { Component: ContractsWidget, needs: ['summary'] },
  'payroll-status': { Component: PayrollWidget, needs: ['summary'] },
  'budget-execution': {
    Component: BudgetExecutionWidget,
    needs: ['summary'],
  },
  'budget-queues': { Component: BudgetQueuesWidget, needs: ['summary'] },
  establishments: { Component: EstablishmentsWidget, needs: ['summary'] },
};

/** Is this block served by `/dashboard/summary` rather than its own query? */
export function isSummaryBacked(key: DashboardWidgetKey): boolean {
  return DASHBOARD_WIDGET_COMPONENTS[key].needs.includes('summary');
}
